import * as React from 'react';
import { useAppTranslation } from '../../services/i18n';
import { confirmInShell } from '../../services/ngShell';
import { SettingNested, SettingRow, SettingsCard } from './SettingRow';
import { mandatoryParamsPatch, SettingsTabProps } from './settingsTab';

interface Props extends SettingsTabProps {
  /** both switches destroy data when turned off, so that direction asks first */
  confirmFlatten?: () => Promise<boolean>;
  confirmTrim?: () => Promise<boolean>;
}

const confirmFlattenInShell = () =>
  confirmInShell('flattenWorkplacesDialog', 'showFlattenWorkplacesDialog');
const confirmTrimInShell = () => confirmInShell('trimWorkplacesDialog', 'showTrimWorkplacesDialog');

export const WorkplaceSettings = ({
  settings,
  isOwner,
  onSave,
  confirmFlatten = confirmFlattenInShell,
  confirmTrim = confirmTrimInShell,
}: Props) => {
  const t = useAppTranslation();
  const isTreeEnabled = settings.workplace_tree_enabled;
  const isDepartmentRequired = !!settings.mandatory_params?.department;

  const handleTreeChange = async (value: boolean) => {
    if (!value && !(await confirmFlatten())) {
      return;
    }
    onSave(
      { workplace_tree_enabled: value },
      `companySettings.workplaces.tree.${value ? 'enabled' : 'disabled'}`
    );
  };

  const handleMultipleChange = async (value: boolean) => {
    if (!value && !(await confirmTrim())) {
      return;
    }
    onSave(
      { multiple_workplaces_enabled: value },
      `companySettings.workplaces.multiple.${value ? 'enabled' : 'disabled'}`
    );
  };

  const allowDepartmentHelper = isTreeEnabled
    ? t('companySettings.workplaces.allowDepartment.helperTree')
    : !isDepartmentRequired
    ? t('companySettings.workplaces.allowDepartment.helperDisabled')
    : undefined;

  return (
    <>
      <SettingsCard
        title={t('companySettings.workplaces.structureTitle')}
        subtitle={t('companySettings.workplaces.structureSubtitle')}
      >
        <SettingRow
          label={t('companySettings.workplaces.tree.label')}
          helper={t('companySettings.workplaces.tree.helper')}
          info={{
            title: t('companySettings.workplaces.tree.infoTitle'),
            text: t('companySettings.workplaces.tree.infoText'),
          }}
          checked={isTreeEnabled}
          disabled={!isOwner}
          onChange={handleTreeChange}
        />
        <SettingRow
          label={t('companySettings.workplaces.multiple.label')}
          helper={t('companySettings.workplaces.multiple.helper')}
          info={{
            title: t('companySettings.workplaces.multiple.infoTitle'),
            text: t('companySettings.workplaces.multiple.infoText'),
          }}
          checked={settings.multiple_workplaces_enabled}
          disabled={!isOwner}
          onChange={handleMultipleChange}
        />
      </SettingsCard>
      <SettingsCard title={t('companySettings.workplaces.registrationTitle')}>
        <SettingRow
          label={t('companySettings.workplaces.department.label')}
          checked={isDepartmentRequired}
          disabled={!isOwner}
          onChange={(value) => onSave(mandatoryParamsPatch(settings, 'department', value))}
        />
        <SettingNested>
          <SettingRow
            label={t('companySettings.workplaces.allowDepartment.label')}
            helper={allowDepartmentHelper}
            checked={settings.user_allowed_to_add_department}
            disabled={!isOwner || isTreeEnabled || !isDepartmentRequired}
            onChange={(value) => onSave({ user_allowed_to_add_department: value })}
          />
        </SettingNested>
      </SettingsCard>
    </>
  );
};
